import { Table, TableContainer } from '@ui-components/neo.tax';
import { useRowSelect, UseRowSelectState } from 'react-table';

import type { SyncedProviders } from '@neo.tax/shared/graphql';

import { customCellClassNames } from './expense-table.styled';
import { useExpenseTableColumns, CreateColumns } from './hooks/useExpenseTableColumns';

const featureHooks = [useRowSelect];

export interface StringIdentifiable {
  id: string;
}

export type ExpenseTableProps<Row extends StringIdentifiable> = {
  createColumns: CreateColumns<Row>;
  data: Row[];
  initialSelectedRowId?: string;
  loadMoreItems: () => Promise<void>;
  loadingFooter: boolean;
  loadingRows: boolean;
  onClickRow: (item: Row) => void;
  onSelectAllRows: (checked: boolean) => void;
  onSelectRow: (checked: boolean, item: Row) => void;
  selectedRowIds: string[];
  syncedProviders?: SyncedProviders;
};

export const ExpenseTable = <Row extends StringIdentifiable>({
  createColumns,
  data,
  initialSelectedRowId,
  loadMoreItems,
  loadingFooter,
  loadingRows,
  onClickRow,
  onSelectAllRows,
  onSelectRow,
  selectedRowIds,
  syncedProviders,
}: ExpenseTableProps<Row>): React.ReactElement => {
  const columns = useExpenseTableColumns({
    selectedRowIds,
    rowCount: data.length,
    onSelectAllRows,
    onSelectRow,
    onClickRow,
    syncedProviders,
    createColumns,
  });

  return (
    <TableContainer height="100%">
      <Table<Row>
        columns={columns}
        data={data}
        isFooterLoading={loadingFooter}
        isBodyLoading={loadingRows}
        featureHooks={featureHooks}
        customCellClassNames={[customCellClassNames<Row>]}
        initialState={
          initialSelectedRowId
            ? ({
                selectedRowIds: {
                  [initialSelectedRowId]: true,
                },
                // The types for `selectedRowIds` in `@types/react-table` are
                // *wrong*: They expect the keys to be of type `IdType<D>`, but
                // that's the type of a *property name* in the row object, not
                // the type of an *ID value*. The type of an ID value is always
                // `string`.
              } as UseRowSelectState<Row>)
            : {}
        }
        tableOptions={{
          getRowId: (row) => row.id,
        }}
        endReached={loadMoreItems}
      />
    </TableContainer>
  );
};
